import React, { HTMLAttributes, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { Button, Popover, Stack, styled, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UploadCompleteDialog from '@features/files/upload/UploadCompleteDialog';
import { useAppSelector } from '@hooks/useStore';
import { selectActiveUserData } from '@state/users/userSlice';
import { AuthContext } from '@contexts';
import { useFilesUpload } from '../hooks';
import { ACCEPTABLE_FILE_TYPES } from '../types';
import { selectActiveDirectory } from '../filesSlice';
import { UploadFileProgress } from './UploadFileProgress';

declare module 'react' {
  interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
    webkitdirectory?: string | boolean | undefined;
  }
}

const UploadIcon = styled(CloudUploadIcon)({
  width: 90,
  height: 60
});

const UploadContainer = styled(Stack)({
  minWidth: 800,
  height: 350,
  border: '3px dashed #b4cbe1',
  borderRadius: '12px'
});

const focusedDropStyle = {
  backgroundColor: '#b4cbe1'
};

const defaultDropStyle = {
  backgroundColor: 'transparent'
};

interface UploadFilesDialogProps {
  anchorEl: Element | null;
  onClose: () => void;
  onRefresh: () => void;
}

const UploadFilesDialog = ({ onClose, anchorEl, onRefresh }: UploadFilesDialogProps) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const { id: activeDirectoryId } = useAppSelector(selectActiveDirectory);
  const {
    uploadFilesIntoDirectory,
    isStarted,
    setIsStarted,
    filesNumber,
    filesSize,
    uploadedFilesNumber,
    setUploadedFilesNumber,
    uploadedFilesSize
  } = useFilesUpload();
  const [directorySelector, setDirectorySelector] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { activeUserId } = useAppSelector(selectActiveUserData);

  const { user } = useContext(AuthContext);
  const currentUserId = user?.getUsername() ?? '';

  const { getRootProps, getInputProps, open: openDialog } = useDropzone({
    accept: ACCEPTABLE_FILE_TYPES,
    onDrop: async (acceptedFiles: FileWithPath[]) => {
      await uploadFilesIntoDirectory(
        activeDirectoryId,
        acceptedFiles,
        (activeUserId || currentUserId) as string,
        () => {
          setShowSuccess(true);
        }
      );
    },
    onDragEnter: () => setIsDragOver(true),
    onDragLeave: () => setIsDragOver(false),
    onDropAccepted: () => setIsDragOver(false),
    noClick: true,
    noKeyboard: true
  });

  const closeSuccessModal = () => {
    setShowSuccess(false);
    setIsStarted(false);
    setUploadedFilesNumber(0);
    onClose();
  };

  return (
    <>
      <Popover
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Stack sx={{ p: 5 }}>
          <UploadContainer
            justifyContent="center"
            {...getRootProps({ style: isDragOver ? focusedDropStyle : defaultDropStyle })}
          >
            <Stack justifyContent="center" alignItems="center">
              <UploadIcon color="primary" />
              <Typography>{t('home.uploadFilesText1')}</Typography>
              <Typography>{t('home.uploadFilesText2')}</Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setDirectorySelector(false);
                  setTimeout(() => openDialog(), 200);
                }}
              >
                {t('buttons.browseFiles')}
              </Button>
              <Typography>{t('home.uploadFilesText2')}</Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setDirectorySelector(true);
                  setTimeout(() => openDialog(), 200);
                }}
              >
                {t('buttons.browseDirectory')}
              </Button>
            </Stack>
            <input
              type="file"
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...getInputProps()}
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...(directorySelector ? { webkitdirectory: '' } : {})}
              multiple
            />
          </UploadContainer>
          {isStarted && (
            <Stack sx={{ mt: 3 }}>
              <UploadFileProgress
                size={filesSize}
                uploadedFilesNumber={uploadedFilesNumber}
                uploadedFilesSize={uploadedFilesSize}
                filesNumber={filesNumber}
              />
            </Stack>
          )}
        </Stack>
      </Popover>
      <UploadCompleteDialog
        message={t('home.uploadingError')}
        closeSuccessModal={closeSuccessModal}
        totalSize={filesSize}
        filesLength={filesNumber}
        onRefresh={onRefresh}
        show={showSuccess}
        closable
      />
    </>
  );
};

export default UploadFilesDialog;
